import React, { useState, useEffect, useRef } from "react";
import DrugAPI, { DrugSearchResult } from "../../services/drug.service";
import type { ReviewMedication } from "../../services/diagnosis.service";

const FREQUENCY_OPTIONS = ["OD", "BD", "TDS", "QID", "SOS"] as const;
const TIMING_OPTIONS = [
  { value: "", label: "— (no timing)" },
  { value: "A/F", label: "After Food (A/F)" },
  { value: "B/F", label: "Before Food (B/F)" },
] as const;

const FREQ_DOSES: Record<string, number> = { OD: 1, BD: 2, TDS: 3, QID: 4, SOS: 0 };

interface MedForm {
  drug_id: string;
  name: string;
  dosage: string;
  frequency: string;
  timing: string;
  duration: string;
  quantity: string;
  instructions: string;
}

const EMPTY_FORM: MedForm = {
  drug_id: "",
  name: "",
  dosage: "",
  frequency: "OD",
  timing: "",
  duration: "",
  quantity: "",
  instructions: "",
};

export interface AddMedicineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (med: Omit<ReviewMedication, "ai_suggested" | "allergy_conflict" | "override_reason">) => void;
  initialValues?: Partial<ReviewMedication>;
}

const AddMedicineModal: React.FC<AddMedicineModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [form, setForm] = useState<MedForm>(EMPTY_FORM);
  const [drugQuery, setDrugQuery] = useState("");
  const [drugResults, setDrugResults] = useState<DrugSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualQty, setManualQty] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (initialValues) {
      setForm({
        drug_id: initialValues.drug_id ?? "",
        name: initialValues.name ?? "",
        dosage: initialValues.dosage ?? "",
        frequency: initialValues.frequency ?? "OD",
        timing: initialValues.timing ?? "",
        duration: initialValues.duration?.replace(" days", "") ?? "",
        quantity: initialValues.quantity != null ? String(initialValues.quantity) : "",
        instructions: initialValues.instructions ?? "",
      });
      setDrugQuery(initialValues.name ?? "");
    } else {
      setForm(EMPTY_FORM);
      setDrugQuery("");
    }
    setManualQty(false);
    setDrugResults([]);
    setShowDropdown(false);
  }, [isOpen, initialValues]);

  // Drug search with debounce
  useEffect(() => {
    if (drugQuery.length < 3) {
      setDrugResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setSearching(true);
      try {
        const results = await DrugAPI.searchDrugs(drugQuery, { signal: abortRef.current.signal });
        setDrugResults(results);
        setShowDropdown(results.length > 0);
      } catch {
        // aborted or network error — ignore
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [drugQuery]);

  // Auto-calculate quantity from frequency × duration
  useEffect(() => {
    if (manualQty) return;
    const doses = FREQ_DOSES[form.frequency] ?? 1;
    const days = parseInt(form.duration) || 0;
    if (doses > 0 && days > 0) {
      setForm(f => ({ ...f, quantity: String(doses * days) }));
    } else {
      setForm(f => ({ ...f, quantity: "" }));
    }
  }, [form.frequency, form.duration, manualQty]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  const selectDrug = (drug: DrugSearchResult) => {
    setForm(f => ({ ...f, drug_id: drug.drug_id, name: drug.drug_name.toUpperCase(), dosage: drug.strength }));
    setDrugQuery(drug.drug_name);
    setShowDropdown(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.dosage.trim() || !form.frequency) return;
    onSave({
      medication_id: initialValues?.medication_id ?? crypto.randomUUID(),
      drug_id: form.drug_id || undefined,
      name: form.name.toUpperCase(),
      dosage: form.dosage.trim(),
      frequency: form.frequency,
      timing: form.timing || undefined,
      duration: form.duration ? `${form.duration} days` : undefined,
      quantity: form.quantity ? parseInt(form.quantity) : undefined,
      instructions: form.instructions.trim() || undefined,
    });
  };

  const ChevronDown = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      <div
        className="relative w-full md:w-auto md:min-w-[480px] md:max-w-lg max-h-[90vh] bg-white rounded-t-3xl md:rounded-2xl shadow-xl flex flex-col animate-slide-up md:animate-none"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {initialValues ? "Edit Medicine" : "Add Medicine"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Fill in medication details</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form id="med-form" onSubmit={handleSubmit} className="flex flex-col gap-4">

            {/* Drug name with search */}
            <div ref={searchRef} className="relative">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Drug Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Search or type drug name"
                value={drugQuery}
                onChange={e => {
                  setDrugQuery(e.target.value);
                  setForm(f => ({ ...f, name: e.target.value.toUpperCase(), drug_id: "" }));
                }}
                required
                className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
              />
              {searching && <p className="text-xs text-slate-400 mt-1">Searching…</p>}
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg py-1 max-h-48 overflow-y-auto">
                  {drugResults.map(d => (
                    <button
                      key={d.drug_id}
                      type="button"
                      onClick={() => selectDrug(d)}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-slate-800">{d.drug_name}</p>
                      <p className="text-xs text-slate-400">{d.generic_name} · {d.strength}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dosage */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Dosage / Strength <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 500mg, 5mg/5ml"
                value={form.dosage}
                onChange={e => setForm(f => ({ ...f, dosage: e.target.value }))}
                required
                className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
              />
            </div>

            {/* Frequency + Timing */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Frequency <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={form.frequency}
                    onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                    required
                    className="w-full h-11 px-4 pr-10 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors appearance-none"
                  >
                    {FREQUENCY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <ChevronDown />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Timing</label>
                <div className="relative">
                  <select
                    value={form.timing}
                    onChange={e => setForm(f => ({ ...f, timing: e.target.value }))}
                    className="w-full h-11 px-4 pr-10 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors appearance-none"
                  >
                    {TIMING_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <ChevronDown />
                  </div>
                </div>
              </div>
            </div>

            {/* Duration + Quantity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Duration (days)</label>
                <input
                  type="number"
                  min={1}
                  placeholder="e.g. 30"
                  value={form.duration}
                  onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Total Qty
                  <span className="ml-1 text-[10px] text-slate-400 font-normal">(auto-calculated)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  placeholder="Total tablets"
                  value={form.quantity}
                  onChange={e => { setManualQty(true); setForm(f => ({ ...f, quantity: e.target.value })); }}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
                />
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Instructions</label>
              <input
                type="text"
                placeholder="e.g. Take with meals, avoid alcohol"
                value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base sm:text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400 transition-colors"
              />
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-3xl md:rounded-b-2xl">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="med-form"
              className="flex-1 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-sky-400 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              {initialValues ? "Save Changes" : "Add Medicine"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AddMedicineModal;
